import React, {useEffect, useRef, useState} from 'react'
import {Box, BoxProps, Flex, FlexProps, useMultiStyleConfig, VStack, StackProps, TextProps} from '@chakra-ui/react'
import {CardPhotoVideo, CtaButton, CustomStyle, RichText} from '_uiComponents'
import {CardPhotoVideoObject} from '_types'
import {useAnalytics, useIsElementInViewport} from '_customHooks'
import {PAGE_DESIGNER_MODULES} from '_utils'
import {toEditorialAnalyticsList} from '_analytics/utils/analyticsUtils'

interface HalfTextHalfPhotoProps {
    /** The Cloudinary media (image or video) shown in one half. Uses CardPhotoVideo in media-only mode. */
    mediaElement?: CardPhotoVideoObject
    title?: string
    text?: string
    ctaText?: string
    ctaLink?: string
    ctaBlank?: boolean
    textAlign?: 'left' | 'center' | 'right'
    /** Vertical position of the text block within its half (SFRA content_v_align; default center). */
    verticalAlign?: 'top' | 'center' | 'bottom'
    /** Horizontal position of the text block within its half (SFRA content_h_align; default center). */
    horizontalAlign?: 'left' | 'center' | 'right'
    /** Content color (hex) applied to the text half; falls back to the theme default text color. */
    contentColor?: string
    /** Section background (hex) applied behind both halves. */
    sectionBackground?: string
    /**
     * Swaps which half comes first (SFRA `reverse_columns` / Figma Image-vs-Text hierarchy). Page Designer
     * may deliver this as a real boolean OR as the string "true"/"false", so it is accepted loosely and
     * normalized to a strict boolean inside the component.
     */
    reverseColumns?: boolean | string
    /** 09AC: enables the media zoom + text fade animations (maps to CardPhotoVideo animation flags). */
    animated?: boolean
    animationDuration?: string
    classes?: string
    extraCSS?: string
    analyticsID?: string
    position: number
    analyticsList?: string
    analyticsName?: string
    /** Above-the-fold priority (first-row module), propagated to the video for eager load. */
    isMainBanner?: boolean
}

/**
 * @name HalfTextHalfPhoto
 *
 * @component
 *
 * @description
 * Page Designer module 09C (static) / 09AC (animated) "Half Text Half Photo (Cloudinary)". Renders a
 * 50/50 split — one half is a Cloudinary image or video, the other half is a centered text block
 * (title / text / CTA). On mobile the halves stack vertically. `reverseColumns` swaps their order.
 *
 * The media half reuses the shared {@link CardPhotoVideo} molecule in media-only mode (no title/cta on
 * the media), so Cloudinary image responsive sources and video player handling stay centralized. The
 * `animated` variant (09AC) toggles CardPhotoVideo's zoom-image / fade-text animation flags — the same
 * mechanism DoubleSame uses for its animated Cloudinary variant. Promo view/click analytics mirror the
 * other Cloudinary modules (DoubleSame / TwoThirdOneThird).
 *
 * See .kiro/specs/pdp-banner-page-designer migration notes for the SFRA field mapping.
 */
const HalfTextHalfPhoto: React.FC<HalfTextHalfPhotoProps> = ({
    mediaElement,
    title,
    text,
    ctaText,
    ctaLink,
    ctaBlank,
    textAlign = 'center',
    verticalAlign = 'center',
    horizontalAlign = 'center',
    contentColor,
    sectionBackground,
    reverseColumns = false,
    animated = false,
    animationDuration,
    classes,
    extraCSS,
    analyticsID,
    position,
    analyticsList,
    analyticsName,
    isMainBanner = false,
}) => {
    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    // Normalize reverse_columns to a strict boolean. Page Designer serializes booleans as the STRINGS
    // "true"/"false"; the string "false" is truthy in JS, so passing it straight through would (wrongly)
    // reverse the layout. Treat only a real `true` or the string "true" as reversed.
    const isReversed = reverseColumns === true || reverseColumns === 'true'
    // 09AC fade-up: the text animation plays once the module scrolls into view (mirrors CardPhotoVideo's
    // `isAnimation* && isSelfVisible` gating), so it never fires above the fold before the user sees it.
    const isTextAnimated = animated && isVisible
    const styles = useMultiStyleConfig('HalfTextHalfPhoto', {
        sectionBackground,
        contentColor,
        reverseColumns: isReversed,
        isTextAnimated,
        animationDuration,
        verticalAlign,
        horizontalAlign,
    }) as Record<string, StackProps & BoxProps & FlexProps & TextProps>
    const [alreadySent, setAlreadySent] = useState(false)
    const analyticsListEditorial = toEditorialAnalyticsList(analyticsList)

    // Distinguish the static (09C) vs animated (09AC) creative in analytics, mirroring DoubleSame's
    // " - STATIC/ANIMATION" suffix convention.
    const creative = `${animated ? PAGE_DESIGNER_MODULES.ANIMATED_HALF_TEXT_HALF_PHOTO_CLOUDINARY : PAGE_DESIGNER_MODULES.HALF_TEXT_HALF_PHOTO_CLOUDINARY}`

    const triggerAnalyticsEventPromoClick = (textBtn: string): void => {
        analyticsEventPromoClick({
            id: analyticsID ? analyticsID : 'null',
            position: String(position),
            creative,
            promoText: textBtn,
            name: analyticsName ? analyticsName : 'null',
        })
    }

    useEffect(() => {
        if (isVisible && !alreadySent) {
            analyticsEventPromoView({
                id: analyticsID ? analyticsID : 'null',
                position,
                creative,
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySent(true)
        }
    }, [isVisible])

    // The media half: reuse CardPhotoVideo for Cloudinary image/video, in media-only mode (title/text/cta
    // live in the dedicated text half, not on the media). Animation flags are toggled by `animated`.
    const mediaProps: CardPhotoVideoObject | undefined = mediaElement && {
        ...mediaElement,
        layout: 'fullOneThird',
        title: undefined,
        text: undefined,
        ctaText: undefined,
        ctaLink: undefined,
        isImgZoom: animated,
        isAnimationTitle: false,
        isAnimationCta: false,
        animationTime: animationDuration,
    }

    return (
        <Box {...styles.wrapper} data-testid="half-text-half-photo" data-animated={animated} ref={targetElement}>
            <CustomStyle css={extraCSS} />
            <Flex {...styles.grid} className={`${classes ?? ''} cc-pd cc-pd-half-text-half-photo`}>
                <Box {...styles.mediaItem} data-testid="half-text-half-photo-media" className="cc-object-container cc-object-media">
                    {mediaProps && (
                        <CardPhotoVideo
                            propsObject={mediaProps}
                            classes={classes}
                            isVisible={isVisible}
                            analyticsList={analyticsListEditorial}
                            position={position}
                            videoSlot="halfTextHalfPhoto"
                            isPriority={isMainBanner}
                        />
                    )}
                </Box>

                <Box {...styles.textItem} data-testid="half-text-half-photo-text" className="cc-object-container cc-object-text">
                    <VStack
                        {...styles.body}
                        className="cc-content-text"
                        textAlign={textAlign}
                        align={textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'}
                    >
                        {title && <RichText as="h2" {...styles.title} className="cc-title" content={title} />}
                        {text && <RichText as="p" {...styles.text} className="cc-description" content={text} />}
                        {ctaText && ctaLink && (
                            <Box className="cc-content-cta">
                                <CtaButton
                                    variant="text"
                                    size="sm"
                                    href={ctaLink}
                                    // Match the text half's fallback: use the authored content color when set,
                                    // otherwise fall back to color.text.default (same token as title/body) so the
                                    // CTA stays dark/visible instead of inheriting CtaButton's white default.
                                    ctaColor={contentColor && contentColor.length ? contentColor : 'color.text.default'}
                                    isAnimated={animated}
                                    animationTime={animationDuration}
                                    openInNewTab={!!ctaBlank}
                                    onClick={() => triggerAnalyticsEventPromoClick(ctaText)}
                                    data-testid="half-text-half-photo-cta"
                                >
                                    {ctaText}
                                </CtaButton>
                            </Box>
                        )}
                    </VStack>
                </Box>
            </Flex>
        </Box>
    )
}

export {HalfTextHalfPhoto}
